// @givmo/app-charity — intake-door vocabulary (CBO-IN4).
//
// Every word this surface renders about a row, a run or a record is SELECTED FROM THE WIRE and lives
// here, in the same idiom as `enrollment-vocab.ts`. Read that file's header for why; this one records
// the four rulings that are specific to the doors, because each is a sentence somebody would
// otherwise write differently under time pressure.
//
// ══ 1. THE ARTIFACT IS THE ONBOARDING RECORD, AND IT IS NOT A FINISH LINE ══════════════════════
//
// What the doors produce is a STACK — one entry for the books being opened, then one for each import
// that brought something in. It grows whenever something real is added, and it never says setup
// finished. No real organization arrives with complete historical books, so a single "you're all set"
// document would be wrong on the first day and every day after; readiness is read from the books'
// own open items, which this surface cannot see and therefore never claims.
//
// ══ 2. A REFUSED RUN IS A RECORDED OUTCOME, RENDERED AS PLAINLY AS A SUCCESSFUL ONE ════════════
//
// Refused is not "didn't finish", not "we'll keep trying", and not an error state to be apologized
// for. The attempt happened, the reason is written on it, and an operator asking why history is
// missing is better served by a row naming the refusal than by an absence. Nothing here retries in
// the background, so no copy may suggest anything is still in progress.
//
// ══ 3. THE HISTORY RAILS ARE NOT AVAILABLE, AND THE HONEST WORD IS NOT "SOON" ══════════════════
//
// Three of the four rails read an export taken out of the system an organization is leaving, and this
// build holds no path for one. The server's own refusal says "temporarily unavailable" — the right
// body for a caller that could be anyone, and the WRONG sentence here, because nothing about it is
// temporary in the sense a person would read. So this surface says what is true: that way of bringing
// history in is not built yet, this is what exists instead, and there is no timing claim attached to
// either. ⚠ NO "CONNECT YOUR BANK" AFFORDANCE, in any wording — an affordance for a rail that does
// not exist is a promise made by a button.
//
// ══ 4. NOTHING HERE IS A PROGRESS BAR ═════════════════════════════════════════════════════════
//
// No step counter, no percentage, no "almost there". What a person is working through is a set of
// questions about their own books, and the honest rendering of that is how many are still waiting —
// which is a fact — rather than how far along they are, which would be a claim about a finish line
// that does not exist.
//
// ⚠ NO FEE OR CONSIDERATION COPY, IN EITHER DIRECTION, as everywhere in this feature. A held slot
// renders nothing.

import type {
  ImportRail,
  ImportRunState,
  IntakeResolution,
  OnboardingRecordKind,
} from './enrollment-backfill-decode';
import type { EnrollmentBadgeTone } from './enrollment-vocab';

/** What one value's state is called and how it is toned. */
export interface ResolutionVocab {
  label: string;
  tone: EnrollmentBadgeTone;
}

/**
 * The four resolution states.
 *
 * ⚠ `pending` AND `declined` READ DIFFERENTLY ON PURPOSE, because they are different facts and an
 * operator chasing a stuck run needs to tell them apart: nobody has looked at a pending row, whereas
 * somebody looked at a declined one and would not answer. They block equally, and the copy for each
 * says so without collapsing them into one word.
 */
const RESOLUTION_VOCAB: Record<IntakeResolution, ResolutionVocab> = {
  pending: { label: 'Waiting for your answer', tone: 'warning' },
  confirmed: { label: 'You confirmed this', tone: 'success' },
  corrected: { label: 'You entered your own', tone: 'success' },
  declined: { label: 'You didn’t answer this', tone: 'warning' },
};

/** The label for a state this build does not recognize — says nothing about what the row IS. */
const UNKNOWN_RESOLUTION: ResolutionVocab = { label: 'Recorded', tone: 'neutral' };

export function resolutionVocab(resolution: IntakeResolution | null): ResolutionVocab {
  return resolution === null ? UNKNOWN_RESOLUTION : RESOLUTION_VOCAB[resolution];
}

/** What one run's state is called, and the sentence that goes with it. */
export interface RunStateVocab {
  label: string;
  description: string;
  tone: EnrollmentBadgeTone;
}

const RUN_STATE_VOCAB: Record<ImportRunState, RunStateVocab> = {
  started: {
    label: 'Started',
    description: 'This attempt was recorded and has not reached an outcome on this page yet.',
    tone: 'info',
  },
  bound: {
    label: 'Recorded',
    description:
      'What this attempt brought in is now in your organization’s books, and it left an onboarding record below.',
    tone: 'success',
  },
  refused: {
    // ⚠ "Refused" and not "failed". Nothing broke: the module declined to act and wrote down why,
    // which is a different event and reads as one.
    label: 'Refused',
    description:
      'This attempt did not go ahead. What was recorded for it is shown below, word for word.',
    tone: 'warning',
  },
};

const UNKNOWN_RUN_STATE: RunStateVocab = {
  label: 'Recorded',
  description:
    'This attempt is in a state this version of the console doesn’t recognize yet. Nothing is wrong with the record itself.',
  tone: 'neutral',
};

export function runStateVocab(state: ImportRunState | null): RunStateVocab {
  return state === null ? UNKNOWN_RUN_STATE : RUN_STATE_VOCAB[state];
}

/** What each door is called and what it actually does. */
export interface RailVocab {
  label: string;
  description: string;
}

const RAIL_VOCAB: Record<ImportRail, RailVocab> = {
  opening_balances: {
    label: 'Opening balances',
    description:
      'One entry stating what each account held on the day your books start. It does not bring across the transactions behind those balances.',
  },
  // ⚠ The three below name a real shape and state plainly that it is not built. Each says what it
  // WOULD read — an export taken out of the old system — because that is what makes the absence
  // legible rather than mysterious.
  qbo_history: {
    label: 'QuickBooks history',
    description:
      'Every transaction carried across from QuickBooks, read from an export taken out of it. This way of bringing history in isn’t built yet.',
  },
  card_activity: {
    label: 'Card activity',
    description:
      'Card transactions carried across from an export of them. This way of bringing history in isn’t built yet.',
  },
  bank_activity: {
    label: 'Bank activity',
    description:
      'Bank transactions carried across from an export of them. This way of bringing history in isn’t built yet.',
  },
};

export function railVocab(rail: ImportRail | null): RailVocab {
  return rail === null
    ? {
        label: 'Recorded',
        description:
          'This way of bringing history in isn’t one this version of the console recognizes yet.',
      }
    : RAIL_VOCAB[rail];
}

/** What one onboarding record is a record of. */
const RECORD_KIND_LABEL: Record<OnboardingRecordKind, string> = {
  provision: 'Books opened',
  import_run: 'History brought in',
};

export function onboardingRecordKindLabel(kind: OnboardingRecordKind | null): string {
  return kind === null ? 'Recorded' : RECORD_KIND_LABEL[kind];
}

/** Which map a row came from — said in the source system's terms, not the engine's. */
const MAP_KIND_LABEL: Record<string, string> = {
  source_account: 'Account in your old system',
  merchant_account: 'Card merchant',
  bank_flow: 'Bank transaction type',
};

export function mapKindLabel(kind: string | null): string {
  return kind === null ? 'Row' : (MAP_KIND_LABEL[kind] ?? 'Row');
}

/** The page and section copy, in one place so every surface agrees on one spelling. */
export const BACKFILL_COPY = {
  pageTitle: 'Bringing in your history',
  pageSubtitle:
    'Your organization’s books exist. This is where what came before them gets added, a piece at a time.',

  /**
   * The gate copy for a record whose books do not exist yet.
   *
   * ⚠ States what has to be true FIRST, not what is wrong. Reaching this page before setup finishes
   * is an ordinary thing to do — the address is guessable and the step above is where a person just
   * was — so it reads as a signpost rather than a refusal.
   */
  notYetTitle: 'Your books aren’t open yet',
  notYetBody:
    'History goes into books that already exist. Finish setting your organization up first, and this page will have somewhere to put things.',
  notYetAction: 'Back to setup',

  /** The choice. */
  choiceTitle: 'How should your history come in?',
  choiceBody:
    'There are two shapes this takes, and they are genuinely different things rather than two settings of one dial. Pick the one that matches what your organization has.',
  /**
   * ⚠ SAYS WHAT CHANGING IT LATER COSTS, before anybody picks. The backend records this once and
   * treats a different second answer as a correction somebody has to make by hand. Copy that let a
   * person discover that afterwards would have hidden the one thing they needed in order to choose.
   */
  choiceOnceHint:
    'This is recorded once. Changing it afterwards isn’t something you can do here — the two shapes post differently, so switching means someone unpicking what the first one already did.',
  choiceAction: 'Record this',
  /** The recorded choice, once it exists. */
  choiceRecordedLabel: 'How your history comes in',

  /** Mapping review. */
  mappingTitle: 'Where each account posts',
  mappingBody:
    'Each row below came out of your old system. Confirming one says the account it posts to is right; correcting it puts yours in its place. Every row is answered on its own — there is no way to answer them together, because a row that posts to the wrong place looks perfectly fine in a total.',
  mappingEmpty: 'No rows are waiting for an answer.',
  mappingProposedLabel: 'Posts to',
  mappingNoProposal: 'Nothing was proposed for this row.',
  mappingCorrectLabel: 'Account it should post to',
  mappingNoteLabel: 'Note (optional)',

  /** Opening balances. */
  openingTitle: 'Opening balances',
  openingBody:
    'Each account below, and what your organization says it held on the day its books start. Answer them one at a time. What you record here becomes a line of an entry that can’t be edited afterwards, so each figure is your own answer rather than a page you agreed with.',
  openingEmpty:
    'No accounts have been put in front of your organization yet. Add the ones its opening balances cover.',
  openingAddTitle: 'Add an account',
  openingAddBody:
    'Adding an account proposes a figure for it. Nothing is recorded against your organization until you answer it below.',
  openingAccountLabel: 'Account code',
  openingProposedLabel: 'Opening balance',
  /**
   * ⚠ SPELLS OUT THE SIGN CONVENTION, because getting it wrong is silent. A trial balance is
   * debit-positive; somebody entering a credit balance as a positive number produces figures that
   * balance perfectly and describe the opposite of their books.
   */
  openingSignHint:
    'Enter what the account holds as a debit. A credit balance goes in with a minus sign in front of it, the same way it would on a trial balance.',
  openingAddAction: 'Add this account',
  openingProposedFor: 'Proposed',
  openingRecordedFor: 'You recorded',
  openingNoFigure: 'No figure was proposed for this account.',
  openingCorrectLabel: 'Your figure',
  openingSourceLabel: 'Where this came from (optional)',

  /** The three answers. Identical wording wherever they appear, and identical weight. */
  confirmAction: 'Confirm',
  correctAction: 'Enter my own',
  declineAction: 'I can’t answer this',
  /**
   * ⚠ DECLINING IS NOT A DEAD END, AND THE COPY SAYS SO WITHOUT SOFTENING IT. It blocks the import
   * exactly as an unanswered row does — that is the whole point — and it is also re-answerable, so a
   * person who could not answer today is not stuck with it forever.
   */
  declineHint:
    'Recording that you can’t answer keeps the import waiting, which is the point of it. You can come back and answer later.',
  cancelAction: 'Cancel',

  /** The run. */
  runTitle: 'Bring it in',
  runBody:
    'This posts what you recorded above into your organization’s books. Every account has to be answered first.',
  runAction: 'Bring it in',
  runNewAttemptAction: 'Start a new attempt',
  /**
   * ⚠ THE ONE SENTENCE THAT EXPLAINS THE TWO CONTROLS, and it is worth its length. A retry re-reads
   * the attempt that was already made rather than making a second one; a new attempt genuinely runs
   * again. On an import over the same window those are different acts with different consequences,
   * and only the person pressing knows which they mean.
   */
  runNewAttemptHint:
    'Trying again reads back the attempt that was already recorded. Starting a new attempt runs it again from the beginning — which, over a period something was already brought in for, adds a second copy of it.',
  runRefusalTitle: 'What was recorded for this attempt',
  runTieWaivedLabel: 'A check was stood down for this attempt',

  /** The history rails, where they are offered and refused. */
  historyUnavailableTitle: 'Not built yet',
  /**
   * ⚠ THE WHOLE TRUTH, IN THE ORDER THAT IS USEFUL: what does not work, what does, and no date.
   * There is deliberately no "coming soon", no "we're working on it", and no control to press —
   * every one of those would be a promise this surface has no standing to make.
   */
  historyUnavailableBody:
    'Carrying every transaction across reads a file taken out of the system your organization is leaving, and there is nowhere to put one yet. Opening balances are what this console can bring in today.',

  /** Onboarding records. */
  recordsTitle: 'Your onboarding records',
  recordsBody:
    'One entry for your books being opened, and one for each import that brought something in. It grows whenever something real is added.',
  /**
   * ⚠ NEVER "you have no records" AS A NEUTRAL FACT. An organization whose books exist always has at
   * least the record of them being opened, so an empty stack means this client could not read it —
   * and saying "nothing yet" would state as a fact about the organization something that is only a
   * fact about the read.
   */
  recordsUnreadable:
    'These couldn’t be read just now. Nothing your organization has recorded is affected.',
  recordsPendingCopyLabel: 'Still being copied to its second home',
  recordsPackLabel: 'Rules these books run on',

  /** Open items — the honest rendering of what is not finished. */
  openItemsTitle: 'Still waiting on you',
  /**
   * ⚠ SURFACED, NEVER SUPPRESSED. The design's rule is that missing history shows up as the books'
   * own open items rather than being tidied away, and that a suspense balance blocking a close is
   * shown as an open item rather than hidden. This surface can only see the questions it asked, so
   * it says exactly that and claims nothing about the books beyond it.
   */
  openItemsBody:
    'These are the questions this page has asked and not had an answer to. Your books may have open items of their own beyond these.',
} as const;
